'use client';
import { Button, Entering, Loading, Snackbar, TabBar, Typography } from 'components';
import { DOCS_LINK } from 'constants/links';
import { ArrowRightIcon, KeyIcon } from 'lib/svgs/Icons';
import { useTranslations } from 'next-intl';
import Link from 'next/link';
import { useCurrentUser } from 'providers/UserProvider/useCurrentUser';
import { useEffect, useMemo, useState } from 'react';
import { KeysEnum } from 'types/KeysEnum';
import ListIntegrations from './_components/ListIntegrations';
import Logs from './_components/Logs';
import TolqAPIKey from './_components/TolqAPIKey';

const IntegrationsPage = () => {
  const t = useTranslations();
  const [loading, setLoading] = useState(true);
  const [hash, setHash] = useState(KeysEnum.Integrations as string);
  const { data: currentUser } = useCurrentUser();

  const tabsNavigation = (hashName: string) => () => setHash(hashName);

  const tabs = [
    {
      label: t('integrations.tabs.integrations'),
      hash: KeysEnum.Integrations,
      onClick: tabsNavigation(KeysEnum.Integrations)
    },
    {
      label: t('integrations.tabs.logs'),
      hash: KeysEnum.Logs,
      onClick: tabsNavigation(KeysEnum.Logs)
    }
  ];

  const activeTab = useMemo(() => {
    const tabIndex = tabs.findIndex((tab) => tab.hash === hash) ?? 0;

    return tabIndex !== -1 ? tabIndex : 0;
  }, [hash, tabs]);

  useEffect(() => {
    setLoading(false);
  }, []);

  const screens = [<ListIntegrations key={KeysEnum.Integrations} />, <Logs key={KeysEnum.Logs} />];

  if (loading) return <Loading />;

  return (
    <>
      <div className="mb-8 pt-10 md:mx-4 lg:mx-8">
        <div className="mb-2 flex items-center justify-between">
          <Entering offset={5} className="flex" position="top">
            <Typography variant="medium7xl" className="!mt-0 flex text-tertiary-100">
              {t('integrations.screen.title')}
            </Typography>
          </Entering>
          <Entering className="flex" delay={0.4} position="top" offset={5}>
            <TolqAPIKey />
          </Entering>
        </div>
        <Entering offset={5} delay={0.2} className="mb-8 flex flex-col gap-4" position="top">
          <Typography variant="light3" className="m-0 text-tertiary-200">
            {t('integrations.screen.paragraph')}
          </Typography>
        </Entering>
        {currentUser?.accessKey && (
          <Entering offset={5} delay={0.6} className="mb-8 flex flex-col gap-4" position="top">
            <Snackbar
              className="border border-solid border-tertiary-400"
              classNameTypographyName="!font-normal"
              icon={<KeyIcon />}
              variant="success"
              name={
                <p
                  dangerouslySetInnerHTML={{
                    __html: t
                      .raw('integrations.tolq_api_key.key.access_key')
                      .replace('{access_key}', currentUser?.accessKey)
                  }}
                />
              }
            />
          </Entering>
        )}
        <Entering offset={5} delay={0.7} className="mb-8 flex flex-col gap-4" position="top">
          <Snackbar
            className="border border-solid border-tertiary-400"
            name={t('integrations.documentation.title')}
            subtitle={t.raw('integrations.documentation.paragraph')}
          >
            <Link href={DOCS_LINK} target="_blank" className="mt-1">
              <Button
                variant="text-small"
                className=""
                label={t('integrations.documentation.go_to_doc')}
                endAdornment={<ArrowRightIcon className="scale-150" />}
              />
            </Link>
          </Snackbar>
        </Entering>
        <Entering offset={5} delay={0.8} className="mb-8 flex flex-col gap-4" position="top">
          <TabBar tabs={tabs} activeTabIndex={activeTab} className="!mt-0" noRedirect>
            <Entering offset={5} delay={0.9} className="mb-8 flex flex-col gap-4" position="top">
              {screens[activeTab]}
            </Entering>
          </TabBar>
        </Entering>
      </div>
    </>
  );
};

export default IntegrationsPage;
